/**
 * Built-in request and response interceptors
 */

import { Interceptor, RpcEnvelope, RpcResponse } from "../types/protocol";

/**
 * Request interceptor for modifying outbound requests
 */
export class RequestInterceptor implements Interceptor {
  public readonly name = "RequestInterceptor";
  public readonly order = 100;

  private headers: Record<string, string> = {};

  /**
   * Add a header to all outbound requests
   */
  addRequestHeader(key: string, value: string): void {
    this.headers[key] = value;
  }

  /**
   * Remove a header from outbound requests
   */
  removeRequestHeader(key: string): void {
    delete this.headers[key];
  }

  async intercept(
    direction: "inbound" | "outbound",
    message: RpcEnvelope | RpcResponse,
    next: () => Promise<any>,
  ): Promise<any> {
    if (direction === "outbound" && this.isRpcEnvelope(message)) {
      // Add headers to outbound requests
      if (Object.keys(this.headers).length > 0) {
        message.headers = {
          ...message.headers,
          ...this.headers,
        };
      }
    }

    return next();
  }

  private isRpcEnvelope(
    message: RpcEnvelope | RpcResponse,
  ): message is RpcEnvelope {
    return "kind" in message;
  }
}

/**
 * Response interceptor for modifying inbound responses
 */
export class ResponseInterceptor implements Interceptor {
  public readonly name = "ResponseInterceptor";
  public readonly order = 200;

  private responseTransforms: Array<(response: RpcResponse) => RpcResponse> =
    [];

  /**
   * Add a response transformation function
   */
  addResponseTransform(
    transform: (response: RpcResponse) => RpcResponse,
  ): void {
    this.responseTransforms.push(transform);
  }

  /**
   * Clear all response transformations
   */
  clearResponseTransforms(): void {
    this.responseTransforms = [];
  }

  async intercept(
    direction: "inbound" | "outbound",
    message: RpcEnvelope | RpcResponse,
    next: () => Promise<any>,
  ): Promise<any> {
    const result = await next();

    if (direction === "inbound" && this.isRpcResponse(message)) {
      // Apply response transformations
      let transformedResponse = message;
      for (const transform of this.responseTransforms) {
        transformedResponse = transform(transformedResponse);
      }
      return transformedResponse;
    }

    return result;
  }

  private isRpcResponse(
    message: RpcEnvelope | RpcResponse,
  ): message is RpcResponse {
    return "ok" in message;
  }
}

/**
 * Timing interceptor for measuring request performance
 */
export class TimingInterceptor implements Interceptor {
  public readonly name = "TimingInterceptor";
  public readonly order = 10; // Run early to capture full timing

  async intercept(
    direction: "inbound" | "outbound",
    message: RpcEnvelope | RpcResponse,
    next: () => Promise<any>,
  ): Promise<any> {
    if (direction === "outbound" && this.isRpcEnvelope(message)) {
      const startTime = performance.now();

      const result = await next();

      const endTime = performance.now();
      const duration = endTime - startTime;

      // Add timing data to the message
      message.timing = {
        startTime,
        endTime,
        duration,
      };

      return result;
    }

    return next();
  }

  private isRpcEnvelope(
    message: RpcEnvelope | RpcResponse,
  ): message is RpcEnvelope {
    return "kind" in message;
  }
}

/**
 * Validation interceptor for ensuring message integrity
 */
export class ValidationInterceptor implements Interceptor {
  public readonly name = "ValidationInterceptor";
  public readonly order = 1; // Run first to catch issues early

  private customRules: Record<string, Array<(message: any) => boolean>> = {
    inbound: [],
    outbound: [],
  };

  /**
   * Add a custom validation rule
   */
  addValidationRule(
    direction: "inbound" | "outbound",
    rule: (message: RpcEnvelope | RpcResponse) => boolean,
  ): void {
    this.customRules[direction].push(rule);
  }

  /**
   * Clear all validation rules for a direction
   */
  clearValidationRules(direction: "inbound" | "outbound"): void {
    this.customRules[direction] = [];
  }

  async intercept(
    direction: "inbound" | "outbound",
    message: RpcEnvelope | RpcResponse,
    next: () => Promise<any>,
  ): Promise<any> {
    // Validate message structure
    if (direction === "outbound" && this.isRpcEnvelope(message)) {
      this.validateOutboundRequest(message);
    } else if (direction === "inbound" && this.isRpcResponse(message)) {
      this.validateInboundResponse(message);
    }

    // Apply custom validation rules
    for (const rule of this.customRules[direction]) {
      rule(message);
    }

    return next();
  }

  private validateOutboundRequest(envelope: RpcEnvelope): void {
    if (!envelope.id) {
      throw new Error("Invalid request: missing id");
    }
    if (!envelope.kind) {
      throw new Error("Invalid request: missing kind");
    }
    if (envelope.kind === "call" && !envelope.service) {
      throw new Error("Invalid request: missing service");
    }
    if (envelope.kind === "call" && !envelope.method) {
      throw new Error("Invalid request: missing method");
    }
    if (!envelope.ts) {
      throw new Error("Invalid request: missing timestamp");
    }
  }

  private validateInboundResponse(response: RpcResponse): void {
    if (!response.id) {
      throw new Error("Invalid response: missing id");
    }
    if (typeof response.ok !== "boolean") {
      throw new Error("Invalid response: missing ok field");
    }
    if (!response.ts) {
      throw new Error("Invalid response: missing timestamp");
    }
  }

  private isRpcEnvelope(
    message: RpcEnvelope | RpcResponse,
  ): message is RpcEnvelope {
    return "kind" in message;
  }

  private isRpcResponse(
    message: RpcEnvelope | RpcResponse,
  ): message is RpcResponse {
    return !("kind" in message);
  }
}
